import React from 'react';
import { Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';
import { Property } from '../../types/property';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface ContractPDFProps {
  property: Property;
  agencyName: string;
  agencyAddress: string;
  brandColor?: string;
}

export const ContractPDF: React.FC<ContractPDFProps> = ({
  property,
  agencyName,
  agencyAddress,
  brandColor = '#0F766E',
}) => {
  const styles = StyleSheet.create({
    page: {
      backgroundColor: '#FFFFFF',
      fontFamily: 'Helvetica',
      padding: 30,
      height: '100%',
    },
    header: {
      marginBottom: 20,
      alignItems: 'center',
    },
    title: {
      fontSize: 20,
      fontWeight: 'bold',
      color: brandColor,
      marginBottom: 5,
      textAlign: 'center',
    },
    date: {
      fontSize: 10,
      color: '#6B7280',
      textAlign: 'right',
      marginBottom: 20,
    },
    section: {
      marginBottom: 20,
    },
    sectionTitle: {
      fontSize: 14,
      fontWeight: 'bold',
      color: '#111827',
      marginBottom: 10,
      textTransform: 'uppercase',
      letterSpacing: 0.8,
      borderLeftWidth: 3,
      borderLeftColor: brandColor,
      paddingLeft: 8,
    },
    paragraph: {
      fontSize: 10,
      color: '#4B5563',
      marginBottom: 8,
      lineHeight: 1.5,
      textAlign: 'justify',
    },
    infoGrid: {
      marginBottom: 10,
    },
    infoRow: {
      flexDirection: 'row',
      marginBottom: 6,
    },
    infoLabel: {
      width: '35%',
      fontSize: 10,
      color: '#6B7280',
      fontWeight: 'bold',
    },
    infoValue: {
      width: '65%',
      fontSize: 10,
      color: '#111827',
    },
    priceBadge: {
      backgroundColor: `${brandColor}20`,
      paddingVertical: 6,
      paddingHorizontal: 15,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: brandColor,
      alignSelf: 'flex-end',
      marginBottom: 15,
    },
    priceText: {
      fontSize: 11,
      fontWeight: 'bold',
      color: brandColor,
    },
    signatures: {
      marginTop: 30,
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    signatureBox: {
      width: '45%',
      borderTopWidth: 1,
      borderTopColor: '#E5E7EB',
      paddingTop: 20,
      alignItems: 'center',
    },
    signatureTitle: {
      fontSize: 11,
      fontWeight: 'bold',
      color: '#111827',
      marginBottom: 5,
    },
    signatureSubtitle: {
      fontSize: 9,
      color: '#6B7280',
      textAlign: 'center',
      marginBottom: 40,
    },
    footer: {
      marginTop: 20,
      textAlign: 'center',
      fontSize: 8,
      color: '#9CA3AF',
      borderTopWidth: 1,
      borderTopColor: '#E5E7EB',
      paddingTop: 10,
    },
  });

  const formatPrice = (price: number) => {
    return Math.floor(price || 0).toLocaleString('fr-FR').replace(/\s/g, ' ') + ' DA';
  };

  // Fonction pour obtenir le type de bien de manière robuste
  const getPropertyType = () => {
    // Priorité à property_category + property_subtype
    if (property.property_category) {
      return property.property_subtype && property.property_subtype !== 'Simple'
        ? `${property.property_category} ${property.property_subtype}`
        : property.property_category;
    }
    // Fallback sur property.type
    return property.type || 'Bien immobilier';
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* En-tête */}
        <View style={styles.header}>
          <Text style={styles.title}>MANDAT DE VENTE EXCLUSIF</Text>
          <Text style={styles.date}>
            {format(new Date(), 'PPP', { locale: fr })}
          </Text>
        </View>

        {/* Partie "ENTRE LES SOUSSIGNÉS" */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Entre les soussignés</Text>

          <Text style={styles.paragraph}>
            {agencyName}, agence immobilière dont le siège social est situé {agencyAddress}, ci-après dénommée "le Mandataire",
          </Text>

          <Text style={styles.paragraph}>ET</Text>

          <Text style={styles.paragraph}>
            {property.owner?.name || property.owner_name || 'Nom non spécifié'}, téléphone : {property.owner?.phone || property.owner_phone || 'Non spécifié'}, email : {property.owner?.email || property.owner_email || 'Non spécifié'}, ci-après dénommé(e) "le Mandant".
          </Text>
        </View>

        {/* Partie "IL A ÉTÉ CONVENU CE QUI SUIT" */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Il a été convenu ce qui suit</Text>

          <Text style={styles.paragraph}>
            Le Mandant donne mandat exclusif au Mandataire de vendre le bien immobilier dont la désignation suit :
          </Text>

          <View style={styles.infoGrid}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Type de bien :</Text>
              <Text style={styles.infoValue}>
                {getPropertyType()}
              </Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Adresse :</Text>
              <Text style={styles.infoValue}>{property.address || 'Non spécifiée'}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Prix net vendeur :</Text>
              <Text style={styles.infoValue}>{formatPrice(property.netPrice || property.net_price || 0)}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Commission agence :</Text>
              <Text style={styles.infoValue}>{formatPrice(property.commission || 0)}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Prix de vente :</Text>
              <View style={styles.priceBadge}>
                <Text style={styles.priceText}>{formatPrice(property.price || 0)}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Partie "DURÉE ET CONDITIONS" */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Durée et conditions</Text>

          <Text style={styles.paragraph}>
            1. Le présent mandat est consenti pour une durée de 12 mois à compter de sa signature.
          </Text>

          <Text style={styles.paragraph}>
            2. Le Mandant s'interdit formellement de vendre le bien par lui-même ou par l'intermédiaire d'un autre mandataire pendant la durée du présent mandat.
          </Text>

          <Text style={styles.paragraph}>
            3. Le Mandant s'engage à ne pas traiter directement avec un client qui lui aurait été présenté par le Mandataire, et ce pendant toute la durée du mandat et dans les 12 mois suivant son expiration.
          </Text>

          <Text style={styles.paragraph}>
            4. La commission de l'agence est fixée à {formatPrice(property.commission || 0)} et sera à la charge de l'acquéreur.
          </Text>
        </View>

        {/* Signatures */}
        <View style={styles.signatures}>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureTitle}>Le Mandant</Text>
            <Text style={styles.signatureSubtitle}>Signature précédée de la mention</Text>
            <Text style={styles.signatureSubtitle}>"Lu et approuvé, bon pour mandat"</Text>
          </View>

          <View style={styles.signatureBox}>
            <Text style={styles.signatureTitle}>Le Mandataire</Text>
            <Text style={styles.signatureSubtitle}>Signature et cachet</Text>
          </View>
        </View>

        {/* Pied de page */}
        <Text style={styles.footer}>
          Mandat de vente exclusif • Document généré le {format(new Date(), 'PPP', { locale: fr })} • Réf: {property.id}
        </Text>
      </Page>
    </Document>
  );
};
